/**
 * F-404-FE-04: PendingNotesPage
 *
 * Lists all DRAFT notes for the authenticated doctor.
 * - Session date, template type, age indicator, patient reference
 * - Tap to resume editing (navigates to NoteEditorPage)
 * - No export action for DRAFT notes
 * - Fetches via GET /api/notes?status=DRAFT
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DoctorLayout } from '@/components/DoctorLayout';
import { api } from '@/lib/api';
import type { NoteListItem } from './types';

// ---- Helpers ----------------------------------------------------------------

function formatRelativeAge(dateStr: string): string {
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHrs = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHrs / 24);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatSessionDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString([], {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ---- Skeleton loading card --------------------------------------------------

function NoteSkeletonCard() {
  return (
    <div className="clinical-card p-5 flex items-center gap-4 animate-pulse">
      <div className="w-10 h-10 skeleton rounded-full flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="skeleton h-4 w-2/5 rounded" />
        <div className="skeleton h-3 w-3/5 rounded" />
        <div className="skeleton h-3 w-1/4 rounded" />
      </div>
      <div className="skeleton h-3 w-16 rounded flex-shrink-0" />
    </div>
  );
}

// ---- Note list item card ----------------------------------------------------

interface NoteCardProps {
  note: NoteListItem;
  onClick: () => void;
}

function NoteCard({ note, onClick }: NoteCardProps) {
  const age = formatRelativeAge(note.updatedAt);
  const sessionDate = formatSessionDate(note.sessionDate);

  return (
    <div className="clinical-card p-5 flex items-center gap-4">
      {/* Icon */}
      <div className="w-10 h-10 rounded-full bg-clinical-teal/10 border border-clinical-teal/20 flex items-center justify-center flex-shrink-0">
        <DocumentEditIcon />
      </div>

      {/* Main tappable content area */}
      <button
        type="button"
        className="flex-1 min-w-0 text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clinical-teal focus-visible:ring-offset-2 rounded-sm"
        onClick={onClick}
        aria-label={`Resume editing ${note.templateName} note from ${sessionDate}`}
      >
        <p className="font-heading text-sm font-semibold text-clinical-charcoal truncate">
          {note.templateName}
        </p>
        <p className="text-sm font-body text-clinical-text-muted mt-0.5 truncate">
          {sessionDate}
          {note.patientRef && (
            <> &middot; <span className="text-clinical-charcoal-soft">{note.patientRef}</span></>
          )}
        </p>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="status-draft text-[10px] px-2 py-0.5">Draft</span>
          <span className="text-xs font-body text-clinical-text-muted">{age}</span>
        </div>
      </button>

      {/* Explicit Edit button anchored to row end */}
      <button
        type="button"
        className="btn-ghost px-3 py-1.5 text-xs min-h-[40px] flex items-center gap-1 flex-shrink-0"
        onClick={onClick}
        aria-label={`Edit note for ${note.templateName}`}
      >
        <PendingPencilIcon />
        Edit
      </button>
    </div>
  );
}

// ---- Empty state ------------------------------------------------------------

function EmptyState() {
  return (
    <div className="flex flex-col items-center gap-4 py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-clinical-cream-dark flex items-center justify-center">
        <InboxIcon />
      </div>
      <div>
        <h2 className="font-heading text-xl font-semibold text-clinical-charcoal">
          No pending notes
        </h2>
        <p className="text-base font-body text-clinical-text-muted mt-1 max-w-xs">
          Draft notes from completed sessions will appear here. All notes are
          up to date.
        </p>
      </div>
    </div>
  );
}

// ---- Main Component --------------------------------------------------------

export function PendingNotesPage() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState<NoteListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchNotes() {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get<{ data: NoteListItem[] }>('/api/notes?status=DRAFT');
        if (!cancelled) setNotes(data ?? []);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to load notes.',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchNotes();
    return () => { cancelled = true; };
  }, []);

  const handleNoteClick = useCallback(
    (noteId: string) => {
      navigate(`/notes/${noteId}/edit`);
    },
    [navigate],
  );

  return (
    <DoctorLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div>
          <h1 className="font-heading text-3xl font-semibold text-clinical-charcoal">
            Pending Notes
          </h1>
          <p className="text-base font-body text-clinical-text-muted mt-1">
            Draft notes awaiting review and completion
          </p>
        </div>

        {/* Content */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <NoteSkeletonCard key={i} />
            ))}
          </div>
        ) : error ? (
          <div className="clinical-card p-6 text-center space-y-3">
            <p className="text-sm font-body text-safety-red">{error}</p>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => window.location.reload()}
            >
              Try again
            </button>
          </div>
        ) : notes.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-3">
            {notes.map((note) => (
              <NoteCard
                key={note.id}
                note={note}
                onClick={() => handleNoteClick(note.id)}
              />
            ))}
          </div>
        )}
      </div>
    </DoctorLayout>
  );
}

// --- Inline icons ---

function DocumentEditIcon() {
  return (
    <svg
      className="w-5 h-5 text-clinical-teal"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      />
    </svg>
  );
}

function PendingPencilIcon() {
  return (
    <svg
      className="w-3 h-3"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125"
      />
    </svg>
  );
}

function InboxIcon() {
  return (
    <svg
      className="w-8 h-8 text-clinical-text-muted"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
      />
    </svg>
  );
}
